// =============================================================================
// ChildTab.jsx
// =============================================================================
// One child's stacked form panel inside RequestWizard.
//
// Renders: the per-child identity header (gender, year level, school name),
// a "What does this child need?" checklist, and the four collapsible category
// sections (fees, uniforms, shoes, laptop) imported from ChildSections.jsx.
//
// NOTE: an old header here said "LEGACY SCHEMA ONLY — do not add new features".
// That referred to the pre-normalisation child schema and is STALE — this IS the
// live step-2 component the wizard renders (verified 2 July 2026).
//
// Pulled out of RequestWizard.jsx as part of the wizard decomposition
// (May 2026).
//
// Props
// ─────
//   child           — the child object slice of form state
//   onChange        — update callback (called with merged copy)
//   user            — logged-in user (drives supplier/state defaults)
//   standardCosts   — cost catalogue (passed down to shoes / laptop)
//   generalDelivery — request-level delivery (used as fallback inside laptop)
//   refNo           — the current request ref_no (used in print + uploads)
//   fieldErrors     — per-field validation errors to highlight
//   sectionBlocks   — seasonal section-availability blocks (portal_config);
//                     ticking a blocked section shows its message instead
// =============================================================================
import React, { useId } from "react";
import { alertDialog, confirmDialog } from "../../services/dialogService";
import { activeSectionBlock, sectionBlockLabel } from "../../services/sectionBlocks";
import {
  DEFAULT_STANDARD_COSTS,
  GENDERS,
  LAPTOP_YEAR_LEVELS,
  stationeryCost,
  UNIFORM_ITEMS,
  WINTER_ITEMS,
} from "../../utils/formConstants";
import {
  ChildFeesSection,
  ChildLaptopSection,
  ChildShoesSection,
  ChildUniformsSection,
  SchoolAutocomplete,
} from "./ChildSections.jsx";

// Checklist of need categories for a child
const CHILD_NEED_KEYS = ["fees", "uniforms", "shoes", "laptop"];
const CHILD_NEED_LABELS = {
  fees: "School Costs & Stationery",
  uniforms: "Uniforms",
  shoes: "Shoes",
  laptop: "Device",
};

// Single child tab — identity fields at top, checklist, expanding sections
export function ChildTab({
  child,
  onChange,
  user,
  standardCosts,
  generalDelivery,
  refNo,
  fieldErrors,
  sectionBlocks,
}) {
  const _isQLD = user && user.state === "QLD";
  const genderId = useId();
  const yearLevelId = useId();

  // A ticked stationery pack is priced by year (primary vs secondary) at the moment it is
  // ticked, so re-price it whenever the year level changes — otherwise a pack ticked under
  // a secondary year (or before any year was chosen) keeps the secondary price after the
  // year is corrected (request 768: Year 3 child at $120, 12 Oct 2026). Unticked rows and
  // every other fee type are left alone.
  function repriceStationery(fees, yearLevel) {
    const costs = Object.assign({}, DEFAULT_STANDARD_COSTS, standardCosts || {});
    return (fees || []).map(function (f) {
      return f.feeType === "Stationery" && parseFloat(f.feeAmount) > 0
        ? Object.assign({}, f, { feeAmount: String(stationeryCost(costs, yearLevel)) })
        : f;
    });
  }

  function upField(field, val) {
    onChange(Object.assign({}, child, { [field]: val }));
  }

  // Which needs are currently selected for this child
  function needSelected(key) {
    if (key === "fees") return !!(child.feesOpen || (child.fees && child.fees.length > 0));
    if (key === "uniforms") return child.uniforms !== null && child.uniforms !== undefined;
    if (key === "shoes") return child.shoes !== null && child.shoes !== undefined;
    if (key === "laptop") return child.laptop !== null && child.laptop !== undefined;
    return false;
  }

  async function toggleNeed(key) {
    // Seasonal section block: ticking a blocked section shows the admin's
    // message instead and the box stays unticked. UN-ticking is always allowed.
    if (!needSelected(key)) {
      const blocked = activeSectionBlock(sectionBlocks, key);
      if (blocked) {
        alertDialog({
          title: `${sectionBlockLabel(key)} — currently unavailable`,
          message: blocked.message,
        });
        return;
      }
    }
    // Device is mutually exclusive with a Device Hire fee. Turning Device ON
    // auto-clears any Device Hire the caseworker already entered (amount + the
    // uploaded agreement), so warn first — don't discard it silently.
    if (key === "laptop" && !needSelected("laptop")) {
      const dhFee = (child.fees || []).find(function (f) {
        return f.feeType === "Device Hire";
      });
      const dhHasValue =
        dhFee &&
        ((parseFloat(dhFee.feeAmount) || 0) > 0 || !!dhFee.brochure || !!dhFee.feeDesc?.trim());
      if (dhHasValue) {
        const proceed = await confirmDialog({
          title: "Remove Device Hire?",
          message:
            "This child has a Device Hire fee entered under School Costs & Stationery. " +
            "A device and Device Hire can't both be requested, so selecting Device will " +
            "remove the Device Hire amount and its uploaded agreement.\n\nProceed?",
          confirmLabel: "Proceed",
          cancelLabel: "Cancel",
        });
        if (!proceed) return;
      }
    }
    const updated = Object.assign({}, child);
    if (key === "fees") {
      updated.feesOpen = !needSelected("fees");
    }
    if (key === "uniforms") {
      updated.uniforms = child.uniforms
        ? null
        : {
            supplier: "",
            isReimbursement: false,
            isColdClimate: false,
            other: "",
            items: (function () {
              const o = {};
              const all = [].concat(UNIFORM_ITEMS, WINTER_ITEMS);
              all.forEach(function (k) {
                o[k] = { cost: "", qty: 0 };
              });
              return o;
            })(),
            authorisedPerson: "",
            authorisedContact: "",
            approvalStatus: "Pending",
          };
    }
    if (key === "shoes") {
      updated.shoes = child.shoes
        ? null
        : { jQty: 0, jCost: "", lQty: 0, lCost: "", approvalStatus: "Pending" };
    }
    if (key === "laptop") {
      updated.laptop = child.laptop
        ? null
        : {
            deviceType: "",
            cost: "",
            byod: null,
            approvalStatus: "Pending",
            deliveryAddress: generalDelivery || "",
          };
    }
    onChange(updated);
  }

  const lbl = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#1A2E2B",
    marginBottom: 5,
  };
  const inp = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    border: `1.5px solid ${"#CFE0DE"}`,
    fontSize: 14,
    fontFamily: "inherit",
    boxSizing: "border-box",
  };
  // The school counts only once it's a DELIBERATE choice: a picked suggestion, a
  // special-option chip, or an explicitly-confirmed free-text name. Typing sets
  // schoolConfirmed=false; a loaded draft (undefined) is grandfathered.
  const schoolChosen = !!child.schoolName?.trim() && child.schoolConfirmed !== false;
  const identityComplete = !!(child.gender && child.yearLevel && schoolChosen);

  return (
    <div>
      {/* Identity fields — one row */}
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 14, marginBottom: 20 }}
      >
        <div>
          <label htmlFor={genderId} style={lbl}>
            Gender <span style={{ color: "#C62828" }}>*</span>
          </label>
          <select
            id={genderId}
            value={child.gender || ""}
            onChange={function (e) {
              upField("gender", e.target.value);
            }}
            style={Object.assign({}, inp, {
              background: fieldErrors?.gender ? "#FFEBEE" : "#FFFFFF",
              border: fieldErrors?.gender ? "1px solid #C62828" : inp.border,
            })}
          >
            <option value="">Select...</option>
            {GENDERS.map(function (g) {
              return React.createElement("option", { key: g, value: g }, g);
            })}
          </select>
        </div>
        <div>
          <label htmlFor={yearLevelId} style={lbl}>
            Year Level <span style={{ color: "#C62828" }}>*</span>
          </label>
          <select
            id={yearLevelId}
            value={child.yearLevel || ""}
            onChange={function (e) {
              const v = e.target.value;
              // Re-price a ticked stationery pack for the new year (see repriceStationery).
              const fees = child.fees ? { fees: repriceStationery(child.fees, v) } : {};
              if (v === "TAFE") {
                onChange(
                  Object.assign({}, child, fees, { yearLevel: v, uniforms: null, shoes: null })
                );
              } else {
                // Leaving a TAFE year: a "TAFE" school is no longer valid, so clear it —
                // the TAFE-school-only-with-TAFE-year rule can't be left violated
                // (John, 9 Aug 2026).
                const patch = Object.assign({}, fees, { yearLevel: v });
                if (child.schoolName === "TAFE") {
                  patch.schoolName = "";
                  patch.schoolConfirmed = false;
                  patch.schoolId = null;
                }
                onChange(Object.assign({}, child, patch));
              }
            }}
            style={Object.assign({}, inp, {
              background: fieldErrors?.yearLevel ? "#FFEBEE" : "#FFFFFF",
              border: fieldErrors?.yearLevel ? "1px solid #C62828" : inp.border,
            })}
          >
            <option value="">Select...</option>
            {LAPTOP_YEAR_LEVELS.map(function (y) {
              return React.createElement("option", { key: y, value: y }, y);
            })}
          </select>
        </div>
        <div>
          {/* SchoolAutocomplete is a custom picker with its own internal input — caption only. */}
          <div style={lbl}>
            School Name <span style={{ color: "#C62828" }}>*</span>
          </div>
          <div
            style={{
              border: fieldErrors?.schoolName ? "1px solid #C62828" : "none",
              borderRadius: 8,
              background: fieldErrors?.schoolName ? "#FFEBEE" : "transparent",
            }}
          >
            <SchoolAutocomplete
              value={child.schoolName || ""}
              confirmed={child.schoolConfirmed !== false}
              onChange={function (v) {
                // Typing = an unconfirmed value → block needs + submit until chosen.
                // Clear any previously-picked schoolId: the name no longer matches a listed school.
                onChange(
                  Object.assign({}, child, {
                    schoolName: v,
                    schoolConfirmed: false,
                    schoolId: null,
                  })
                );
              }}
              onConfirm={function (v, schoolId) {
                // Picked / chip / free-text confirm → a deliberate choice. schoolId is set
                // ONLY for a listed school (the BYOD gate); chips / "Other" / free text → null.
                onChange(
                  Object.assign({}, child, {
                    schoolName: v,
                    schoolConfirmed: true,
                    schoolId: schoolId ?? null,
                  })
                );
              }}
              state={user?.state}
              // Year level gates the picker: disabled until Gender + Year Level are set;
              // drives the phase filter + the TAFE-chip rule (John, 9 Aug 2026).
              yearLevel={child.yearLevel}
              disabled={!(child.gender && child.yearLevel)}
              placeholder="Start typing school name..."
            />
          </div>
        </div>
      </div>

      {/* Needs checklist */}
      <div
        style={{
          background: identityComplete ? "#F5F7F7" : "#FAFAFA",
          border:
            "1px solid " +
            (fieldErrors?.needs ? "#C62828" : identityComplete ? "#CFE0DE" : "#E0E0E0"),
          borderRadius: 10,
          padding: "14px 16px",
          marginBottom: 20,
          opacity: identityComplete ? 1 : 0.5,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: fieldErrors?.needs ? "#C62828" : "#00695C",
            marginBottom: identityComplete ? 10 : 4,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          What does this child need?
        </div>
        {!identityComplete && (
          <div
            style={{
              fontSize: 12,
              color: "#C62828",
              fontWeight: 600,
              background: "#FFEBEE",
              border: "1px solid #EF9A9A",
              borderRadius: 6,
              padding: "6px 10px",
              marginBottom: 8,
            }}
          >
            ⚠ Please complete Gender, Year Level and School Name above before selecting needs.
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            pointerEvents: identityComplete ? "auto" : "none",
          }}
        >
          {CHILD_NEED_KEYS.filter(function (key) {
            if (child.yearLevel === "TAFE" && (key === "uniforms" || key === "shoes")) return false;
            return true;
          }).map(function (key) {
            const sel = needSelected(key);
            return (
              <label
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: sel ? "#E0F2F1" : "#FFFFFF",
                  border: `1px solid ${sel ? "#00897B" : "#CFE0DE"}`,
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: identityComplete ? "pointer" : "not-allowed",
                  color: sel ? "#00695C" : "#1A2E2B",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!sel}
                  onChange={function () {
                    if (identityComplete) toggleNeed(key);
                  }}
                  style={{ accentColor: "#00897B" }}
                />
                {CHILD_NEED_LABELS[key]}
              </label>
            );
          })}
        </div>
      </div>

      {/* Category sections */}
      {needSelected("fees") && (
        <ChildFeesSection
          fees={child.fees || []}
          onChange={function (newFees) {
            onChange(Object.assign({}, child, { fees: newFees }));
          }}
          hasError={!!fieldErrors?.fees}
          gender={child.gender}
          yearLevel={child.yearLevel}
          schoolName={child.schoolName}
          user={user}
          standardCosts={standardCosts}
          refNo={refNo}
          hasLaptop={!!child.laptop}
        />
      )}
      {needSelected("uniforms") && (
        <ChildUniformsSection
          uniforms={child.uniforms}
          onChange={function (updated) {
            onChange(Object.assign({}, child, { uniforms: updated }));
          }}
          hasError={!!fieldErrors?.uniforms}
          gender={child.gender}
          yearLevel={child.yearLevel}
          schoolName={child.schoolName}
          shelterName={user?.shelterName}
          refNo={"Draft"}
          user={user}
        />
      )}
      {needSelected("shoes") && (
        <ChildShoesSection
          shoes={child.shoes}
          onChange={function (updated) {
            onChange(Object.assign({}, child, { shoes: updated }));
          }}
          yearLevel={child.yearLevel}
          user={user}
          standardCosts={standardCosts}
          hasError={!!fieldErrors?.shoes}
        />
      )}
      {needSelected("laptop") && (
        <ChildLaptopSection
          laptop={child.laptop}
          onChange={function (updated) {
            onChange(Object.assign({}, child, { laptop: updated }));
          }}
          hasError={!!fieldErrors?.laptop}
          yearLevel={child.yearLevel}
          schoolName={child.schoolName}
          user={user}
          standardCosts={standardCosts}
          deliveryAddresses={user?.deliveryAddresses}
          generalDelivery={generalDelivery}
        />
      )}
    </div>
  );
}
